import {
  type InvestigationExport,
  type InvestigationImportResult,
  exportInvestigations,
  importInvestigations,
} from "@/app/providers/services";
import { useNotification } from "@/common/notifications";
import { useDownload } from "@/hooks/use-download";
import ConfirmDialog from "@imminently/decisively-ui/ConfirmDialog";
import type React from "react";
import { useRef, useState } from "react";
import { investigationExportFileName, parseInvestigationExport, transferErrorMessage } from "./investigationTransfer";

export type InvestigationExportScope = "page" | "all";

/**
 * Export the current investigation page or all pages to a JSON file, and import pages from one.
 * Exports read the saved pages from the server, including HTML card content and each HTML card's
 * saved project-level state. Render `elements` once: it holds the file input and the import
 * confirmation that `chooseImportFile` opens.
 */
export function useInvestigationTransfer({
  modelId,
  investigationId,
  dirty,
  onImported,
}: {
  modelId: string;
  /** The page shown in the toolbar; a "page" export needs one. */
  investigationId?: string;
  /** Unsaved edits aren't on the server, so they can't be exported and an import would discard them. */
  dirty: boolean;
  onImported: (result: InvestigationImportResult) => void | Promise<void>;
}) {
  const notify = useNotification();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingImport, setPendingImport] = useState<InvestigationExport | null>(null);
  const [importing, setImporting] = useState(false);

  const exportPages = useDownload(
    async (_onProgress, scope: InvestigationExportScope) => {
      try {
        const response = await exportInvestigations.submit({
          params: { id: modelId },
          query: scope === "page" ? { investigationId } : {},
        });
        return {
          filename: investigationExportFileName(response.data, scope),
          content: JSON.stringify(response.data, null, 2),
          mimeType: "application/json",
        };
      } catch (error) {
        console.error(error);
        notify.error(transferErrorMessage(error, "Failed to export investigations"));
        return null;
      }
    },
    {
      pending: "Exporting",
      success: dirty
        ? "Exported the last saved version. Unsaved changes were not included."
        : "Investigations exported",
      failure: "Failed to export investigations",
    },
  );

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so choosing the same file again still fires a change event.
    event.target.value = "";
    if (!file) return;
    try {
      setPendingImport(parseInvestigationExport(await file.text()));
    } catch (error) {
      notify.error(transferErrorMessage(error, "Could not read the file"));
    }
  };

  const confirmImport = async () => {
    if (!pendingImport) return;
    setImporting(true);
    try {
      const response = await importInvestigations.submit({
        params: { id: modelId },
        body: { payload: pendingImport },
      });
      setPendingImport(null);
      await onImported(response.data);
      const count = response.data.investigations.length;
      notify.success(`Imported ${count} ${count === 1 ? "page" : "pages"}`);
    } catch (error) {
      console.error(error);
      notify.error(transferErrorMessage(error, "Failed to import investigations"));
    } finally {
      setImporting(false);
    }
  };

  const pages = pendingImport?.investigations || [];
  const replacesDefault = pages.some((page) => page.isDefault);
  const addedCount = pages.filter((page) => !page.isDefault).length;

  const elements = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={handleFileChange}
      />
      <ConfirmDialog
        open={Boolean(pendingImport)}
        onOpenChange={(open) => {
          if (!open && !importing) setPendingImport(null);
        }}
        title={`Import ${pages.length === 1 ? "1 page" : `${pages.length} pages`}?`}
        description={
          // Rendered inside a <p>, so only inline elements here.
          <span className="flex flex-col gap-2">
            <span className="font-semibold text-foreground">
              {pages.map((page) => (page.isDefault ? "Decision (default page)" : page.name)).join(", ")}
            </span>
            {addedCount > 0 ? (
              <span>
                {addedCount === 1 ? "This page is" : "These pages are"} added alongside your existing pages. Names that
                are already taken get "(imported)" added.
              </span>
            ) : null}
            {replacesDefault ? (
              <span>The Decision page's cards and saved card state will be replaced with the imported ones.</span>
            ) : null}
            {dirty ? <span>Your unsaved changes to the current page will be discarded.</span> : null}
          </span>
        }
        confirmLabel="Import"
        confirmIntent={replacesDefault ? "destructive" : "primary"}
        loading={importing}
        onConfirm={confirmImport}
      />
    </>
  );

  return {
    exportPages,
    chooseImportFile: () => fileInputRef.current?.click(),
    elements,
  };
}
